import { useCallback, useRef, useState } from "react";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiErrors";
import { reportClientError } from "@/lib/errorReporter";
import { invalidateTrips } from "@/lib/cacheInvalidators";
import { useAltivMutation } from "@/hooks/useAltivMutation";
import { useConfirm } from "@/lib/confirm";
import { paxFullName } from "@/lib/newTripConstants";
import { validateLegs, buildLegPayload } from "@/lib/newTripValidation";

// Encapsulates everything that happens once the user clicks "Crear viaje":
// per-leg validation, expired-passport gating, AOG date overlap check,
// schedule-overlap check (skippable via forceSchedule), docs precheck
// dialog, the actual POST (create or per-leg append), and external-crew
// auto-save.
//
// Caller wires:
//   legs, aircraftId, selectedOwner, tripStatus, userRole, temporaryCrew
//   internationalPassportConflicts  (computed at consumer site)
//   appendToTrip                    (null = create mode, object = append mode)
//   t, clearDraft, onSuccess, onValidationError(legIndex)
//
// Returns:
//   submitTrip(e, { forceScheduleOverride })  — call from onSubmit
//   aogConflict, scheduleConflict, forceSchedule, setForceSchedule
//   resetAogConflict, resetScheduleConflict
//   isSubmitting, error
//   resetDocsConfirmed()  — re-arm the docs warning (passenger set changed)
export default function useTripSubmit({
  legs,
  aircraftId,
  selectedOwner,
  tripStatus,
  userRole,
  temporaryCrew,
  internationalPassportConflicts,
  appendToTrip,
  t,
  clearDraft,
  onSuccess,
  onValidationError,
  onLegsCommitted,
}) {
  const confirm = useConfirm();
  const isAppendMode = !!appendToTrip;
  const [error, setError] = useState(null);
  // Busy flag covering the pre-creation server checks (AOG / schedule / docs).
  // `createTripMutation.isPending` only flips once the actual POST starts, so
  // without this the button looked frozen during the checks — user clicks,
  // nothing visible happens until the create begins. This prende el estado
  // ocupado desde el primer click.
  const [isChecking, setIsChecking] = useState(false);
  const [aogConflict, setAogConflict] = useState(null);
  const [scheduleConflict, setScheduleConflict] = useState(null);
  const [forceSchedule, setForceSchedule] = useState(false);
  const docsConfirmedRef = useRef(false);

  const resetDocsConfirmed = useCallback(() => {
    docsConfirmedRef.current = false;
  }, []);

  const createTripMutation = useAltivMutation({
    mutationFn: async ({ tripData, externalNames }) => {
      if (isAppendMode) {
        // Append legs one by one. Each POST is a separate commit, so if leg N
        // fails the first N-1 are already saved. Drop the committed ones from
        // the form and re-throw, so a retry sends only what's left instead of
        // re-posting (and duplicating) the ones that already went in.
        for (let i = 0; i < tripData.legs.length; i++) {
          try {
            await api.post(`/trips/${appendToTrip.trip_id}/legs`, tripData.legs[i]);
          } catch (err) {
            if (i > 0) onLegsCommitted?.(i);
            throw err;
          }
        }
      } else {
        await api.post("/trips", tripData);
      }
      if (externalNames.length > 0) {
        await Promise.all(
          externalNames.map((name) =>
            api
              .post(`/aircraft/${aircraftId}/temporary-crew`, { name, phone: "", email: "" })
              .catch((err) => {
                reportClientError({
                  kind: "frontend_temp_crew_save_failed",
                  severity: "warning",
                  message: `temporary-crew POST failed: ${err?.message || ""}`,
                  stack: err?.stack || "",
                  extra: { aircraftId, name },
                });
              })
          )
        );
      }
    },
    invalidates: ["/trips", "/legs", "/calendar", "/dashboard"],
    onSuccess: () => {
      clearDraft?.();
      invalidateTrips();
      onSuccess?.();
    },
    onError: (err) => {
      setError(apiErrorMessage(err, t));
    },
  });

  const submitTrip = useCallback(
    async (e, { forceScheduleOverride = false } = {}) => {
      if (e && e.preventDefault) e.preventDefault();
      setError(null);

      if (!aircraftId) {
        setError(t("newTrip.errors.selectAircraft"));
        return;
      }
      if (legs.length === 0) {
        setError(t("newTrip.errors.legRequired"));
        return;
      }

      const validationFail = validateLegs(legs, userRole, t);
      if (validationFail) {
        // Bug 3+4 audit: mostrar TODOS los errores en un solo submit (antes
        // era de a uno) y guiar al usuario al primer leg con problema.
        const all = validationFail.errors || [{ error: validationFail.error }];
        setError(all.map((e) => e.error).join("\n"));
        onValidationError?.(validationFail.legIndex);
        return;
      }

      if (internationalPassportConflicts.length > 0 && userRole !== "owner") {
        const idx = legs.findIndex((l) => l.is_international);
        if (idx >= 0) onValidationError?.(idx);
        return;
      }

      // ── Pre-creation server checks (AOG / schedule / docs) ──
      // Antes eran tres round-trips EN SERIE: el botón quedaba inerte hasta
      // que terminaba el último, ~3 viajes de red apilados. Ahora se disparan
      // EN PARALELO y se evalúan por prioridad (AOG > horario > docs),
      // colapsando los 3 viajes en 1. Son independientes y de sólo lectura.
      // `isChecking` prende el botón ocupado desde el primer click.
      setIsChecking(true);
      try {
        const depDates = legs.map((l) => l.departure_date).filter(Boolean);
        const aogPromise =
          !aogConflict && aircraftId && depDates.length > 0
            ? api
                .post("/calendar/check-conflicts", {
                  type: "trip",
                  aircraft_id: aircraftId,
                  start_date: depDates.slice().sort()[0],
                  end_date: depDates.slice().sort().pop(),
                })
                .then((res) => res.data?.conflicts || [])
                .catch(() => [])
            : Promise.resolve([]);

        const proposedLegs = legs
          .filter(
            (l) => l.origin && l.destination && l.departure_date && (l.time_tbd || l.departure_time)
          )
          .map((l) => ({
            origin: (l.origin || "").toUpperCase(),
            destination: (l.destination || "").toUpperCase(),
            departure_time: l.time_tbd
              ? `${l.departure_date}T00:00`
              : `${l.departure_date}T${l.departure_time}`,
            arrival_time:
              l.arrival_date && l.arrival_time
                ? `${l.arrival_date}T${l.arrival_time}`
                : l.time_tbd
                  ? `${l.departure_date}T23:59`
                  : null,
          }));
        const schedulePromise =
          !forceSchedule && !forceScheduleOverride && aircraftId && proposedLegs.length > 0
            ? api
                .post("/trips/check-schedule-overlap", {
                  aircraft_id: aircraftId,
                  proposed_legs: proposedLegs,
                  ...(isAppendMode ? { exclude_trip_id: appendToTrip.trip_id } : {}),
                })
                .then((res) => res.data?.conflicts || [])
                .catch(() => [])
            : Promise.resolve([]);

        const docsPromise = !docsConfirmedRef.current
          ? api
              .post("/trips/precheck-docs", {
                aircraft_id: aircraftId,
                is_international: legs.some((l) => l.is_international),
                departure_date: (legs[0]?.departure_time || "").slice(0, 10),
                crew_user_ids: legs.flatMap((l) =>
                  (l.crew || []).filter((c) => c.user_id).map((c) => c.user_id)
                ),
              })
              .then((res) => res.data || null)
              .catch((err) => {
                console.warn("precheck-docs failed, continuing trip creation", err);
                return null;
              })
          : Promise.resolve(null);

        const [aogConflicts, scheduleConflicts, docsResult] = await Promise.all([
          aogPromise,
          schedulePromise,
          docsPromise,
        ]);

        // Prioridad de avisos, idéntica al flujo serial anterior:
        // AOG (hard stop) → horario (gateado por force) → documentos (confirm).
        if (aogConflicts.length > 0) {
          setAogConflict(aogConflicts);
          return;
        }
        if (scheduleConflicts.length > 0) {
          setScheduleConflict(scheduleConflicts);
          return;
        }
        if (docsResult?.has_warnings) {
          const lines = [];
          (docsResult.expired_aircraft_docs || []).forEach((d) =>
            lines.push(`• Aircraft: ${d.name} (vencido ${d.expiration_date})`)
          );
          (docsResult.expired_crew_docs || []).forEach((d) =>
            lines.push(`• Crew ${d.user_name}: ${d.doc_name} (vencido ${d.expiration_date})`)
          );
          const ok = await confirm({
            message: `⚠ Documentos vencidos detectados — el trip se creará pero quedará CRÍTICO en ops queue:\n\n${lines.join("\n")}\n\n¿Continuar?`,
            tone: "danger",
            confirmLabel: t("common.confirm"),
          });
          if (!ok) return;
          docsConfirmedRef.current = true;
        }

        const tripData = {
          aircraft_id: aircraftId,
          owner_id: selectedOwner || null,
          schedule_conflict: forceSchedule || forceScheduleOverride,
          // El banner "Copiar del tramo anterior" ahora es per-tramo: el modal
          // materializa pax y tripulación dentro de cada tramo (clonado al
          // agregar / re-derivado al alternar el toggle). Por eso AMBOS fallbacks
          // de herencia del backend van apagados — lo enviado es la verdad, y un
          // tramo con el toggle apagado debe quedarse vacío (no re-heredar).
          inherit_passengers: false,
          inherit_crew: false,
          legs: legs.map((leg) => buildLegPayload(leg, tripStatus, paxFullName)),
        };

        const existingNames = new Set(temporaryCrew.map((tc) => tc.name.toLowerCase().trim()));
        const newNames = new Set();
        legs.forEach((leg) => {
          (leg.external_crew || []).forEach((ec) => {
            const name = (ec.name || "").trim();
            if (name && !existingNames.has(name.toLowerCase())) newNames.add(name);
          });
        });

        createTripMutation.mutate({ tripData, externalNames: Array.from(newNames) });
      } catch (err) {
        setError(apiErrorMessage(err, t));
      } finally {
        setIsChecking(false);
      }
    },
    [
      legs,
      aircraftId,
      selectedOwner,
      tripStatus,
      userRole,
      temporaryCrew,
      internationalPassportConflicts,
      isAppendMode,
      appendToTrip,
      aogConflict,
      forceSchedule,
      confirm,
      createTripMutation,
      t,
      onValidationError,
    ]
  );

  return {
    submitTrip,
    error,
    setError,
    aogConflict,
    setAogConflict,
    scheduleConflict,
    setScheduleConflict,
    forceSchedule,
    setForceSchedule,
    isSubmitting: isChecking || createTripMutation.isPending,
    resetDocsConfirmed,
  };
}
